import { API_SCOPES, OUTGOING_EVENTS, type ApiScope } from '@beta-assur/shared';
import { useState, type FormEvent } from 'react';
import { ConfirmButton } from '../../components/confirm';
import { Field } from '../../components/page';
import { ActionError, Empty, QueryView } from '../../components/states';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox, Input } from '../../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table';
import { instant } from '../../lib/format';
import { useI18n } from '../../lib/i18n';
import { useApiMutation, useApiQuery } from '../../lib/query';
import { useTenant } from '../../lib/referentials';

// Développeurs (BA-048 à BA-051, spec 10) : clés d'API et journal des appels, webhooks
// sortants et leurs livraisons, exemples d'appel. Secrets affichés une seule fois.

interface ApiKey {
  id: string;
  label: string;
  prefix: string | null;
  scopes: ApiScope[];
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}
interface Subscription {
  id: string;
  url: string;
  description: string | null;
  events: string[];
  active: boolean;
  createdAt: string;
}
interface Delivery {
  id: string;
  subscriptionId: string;
  eventType: string;
  status: 'pending' | 'delivered' | 'failed' | 'undelivered';
  attempts: number;
  lastStatusCode: number | null;
  createdAt: string;
}

const DELIVERY_TONE = {
  pending: 'info',
  delivered: 'success',
  failed: 'warning',
  undelivered: 'danger',
} as const;

/** Secret affiché une seule fois, avec copie. */
function OneTimeSecret({ label, value }: { label: string; value: string }) {
  const t = useI18n().t;
  return (
    <div role="status" className="rounded-lg border border-warning bg-warning/10 p-3 text-sm">
      <p className="font-medium">{label}</p>
      <code className="mt-1 block break-all font-mono text-xs" data-testid="secret">
        {value}
      </code>
      <p className="mt-1 text-xs text-muted-foreground">{t.developers.shownOnce}</p>
    </div>
  );
}

function ApiKeys() {
  const { t, language } = useI18n();
  const tz = useTenant().data?.timezone;
  const keys = useApiQuery<{ items: ApiKey[] }>('/v1/api-keys');
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>(['read']);
  const [created, setCreated] = useState<string | null>(null);
  const create = useApiMutation<{ label: string; scopes: ApiScope[] }, ApiKey & { key: string }>(
    'POST',
    '/v1/api-keys',
    {
      invalidate: ['/v1/api-keys'],
      onSuccess: (key) => {
        setCreated(key.key);
        setLabel('');
      },
    },
  );
  const revoke = useApiMutation<string>('DELETE', (id) => `/v1/api-keys/${id}`, {
    invalidate: ['/v1/api-keys'],
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (label.trim()) create.mutate({ label: label.trim(), scopes });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.developers.keys}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
          <Field label={t.developers.keyLabel}>
            <Input value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />
          </Field>
          <fieldset className="flex flex-wrap gap-3">
            <legend className="sr-only">{t.developers.scopes}</legend>
            {API_SCOPES.map((scope) => (
              <label key={scope} className="flex items-center gap-1 text-sm">
                <Checkbox
                  checked={scopes.includes(scope)}
                  onChange={(e) =>
                    setScopes((s) =>
                      e.target.checked ? [...s, scope] : s.filter((x) => x !== scope),
                    )
                  }
                />
                {t.developers.scopeLabels[scope]}
              </label>
            ))}
          </fieldset>
          <Button type="submit" disabled={!label.trim() || scopes.length === 0 || create.isPending}>
            {t.developers.createKey}
          </Button>
        </form>
        <ActionError error={create.error} />
        {created ? <OneTimeSecret label={t.developers.newKey} value={created} /> : null}
      </CardContent>
      <QueryView query={keys}>
        {(data) =>
          data.items.length === 0 ? (
            <Empty />
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>{t.developers.keyLabel}</TH>
                  <TH>{t.developers.prefix}</TH>
                  <TH>{t.developers.scopes}</TH>
                  <TH>{t.developers.lastUsed}</TH>
                  <TH>{t.common.actions}</TH>
                </tr>
              </THead>
              <TBody>
                {data.items.map((k) => (
                  <TR key={k.id}>
                    <TD>{k.label}</TD>
                    <TD className="font-mono text-xs">{k.prefix ?? '-'}</TD>
                    <TD>{k.scopes.map((s) => t.developers.scopeLabels[s]).join(', ')}</TD>
                    <TD>
                      {k.lastUsedAt ? instant(k.lastUsedAt, tz, language) : t.developers.never}
                    </TD>
                    <TD>
                      {k.revokedAt ? (
                        <Badge tone="neutral">{t.developers.revoked}</Badge>
                      ) : (
                        <ConfirmButton
                          label={t.developers.revoke}
                          title={t.developers.revoke}
                          message={t.developers.revokeMessage}
                          variant="destructive"
                          onConfirm={() => revoke.mutateAsync(k.id)}
                        />
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryView>
      <CurlExample />
    </Card>
  );
}

function CurlExample() {
  const t = useI18n().t;
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return (
    <CardContent className="border-t border-border">
      <p className="mb-2 text-sm font-medium">{t.developers.example}</p>
      <pre className="overflow-x-auto rounded-lg bg-muted p-3 text-xs" dir="ltr">
        {`curl ${origin}/v1/public/installments?status=overdue \\
  -H "Authorization: Bearer <clé>"`}
      </pre>
      <p className="mt-2 text-xs text-muted-foreground">
        {t.developers.docs}{' '}
        <a className="underline" href="/v1/public/openapi.json" target="_blank" rel="noreferrer">
          openapi.json
        </a>
      </p>
    </CardContent>
  );
}

function RequestLog() {
  const { t, language } = useI18n();
  const tz = useTenant().data?.timezone;
  const log = useApiQuery<{
    items: {
      id: string;
      method: string;
      path: string;
      status: number;
      durationMs: number;
      at: string;
    }[];
  }>('/v1/api-keys/requests');
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.developers.requests}</CardTitle>
      </CardHeader>
      <QueryView query={log}>
        {(data) =>
          data.items.length === 0 ? (
            <Empty />
          ) : (
            <Table>
              <TBody>
                {data.items.map((r) => (
                  <TR key={r.id}>
                    <TD className="whitespace-nowrap">{instant(r.at, tz, language)}</TD>
                    <TD className="font-mono text-xs" dir="ltr">
                      {r.method} {r.path}
                    </TD>
                    <TD>
                      <Badge
                        tone={r.status < 400 ? 'success' : r.status < 500 ? 'warning' : 'danger'}
                      >
                        {r.status}
                      </Badge>
                    </TD>
                    <TD className="text-end tabular-nums">{r.durationMs} ms</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryView>
    </Card>
  );
}

function Webhooks() {
  const { t, language, count } = useI18n();
  const tz = useTenant().data?.timezone;
  const subscriptions = useApiQuery<{ items: Subscription[] }>('/v1/outgoing-webhooks');
  const deliveries = useApiQuery<{ items: Delivery[] }>('/v1/outgoing-webhooks/deliveries', {
    limit: '50',
  });
  const [url, setUrl] = useState('https://');
  const [events, setEvents] = useState<string[]>(['payment.confirmed']);
  const [secret, setSecret] = useState<string | null>(null);
  const create = useApiMutation<
    { url: string; events: string[] },
    Subscription & { secret: string }
  >('POST', '/v1/outgoing-webhooks', {
    invalidate: ['/v1/outgoing-webhooks'],
    onSuccess: (s) => setSecret(s.secret),
  });
  const toggle = useApiMutation<{ id: string; active: boolean }>(
    'PATCH',
    (b) => `/v1/outgoing-webhooks/${b.id}`,
    {
      invalidate: ['/v1/outgoing-webhooks'],
    },
  );
  const remove = useApiMutation<string>('DELETE', (id) => `/v1/outgoing-webhooks/${id}`, {
    invalidate: ['/v1/outgoing-webhooks'],
  });
  const replay = useApiMutation<string>(
    'POST',
    (id) => `/v1/outgoing-webhooks/deliveries/${id}/replay`,
    {
      invalidate: ['/v1/outgoing-webhooks/deliveries'],
    },
  );

  function submit(event: FormEvent) {
    event.preventDefault();
    create.mutate({ url: url.trim(), events });
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t.developers.webhooks}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <form onSubmit={submit} className="flex flex-col gap-3">
            <Field label={t.developers.url}>
              <Input type="url" dir="ltr" value={url} onChange={(e) => setUrl(e.target.value)} />
            </Field>
            <fieldset className="flex flex-wrap gap-3">
              <legend className="mb-1 text-sm font-medium">{t.developers.events}</legend>
              {OUTGOING_EVENTS.map((e) => (
                <label key={e} className="flex items-center gap-1 font-mono text-xs" dir="ltr">
                  <Checkbox
                    checked={events.includes(e)}
                    onChange={(ev) =>
                      setEvents((s) => (ev.target.checked ? [...s, e] : s.filter((x) => x !== e)))
                    }
                  />
                  {e}
                </label>
              ))}
            </fieldset>
            <div>
              <Button
                type="submit"
                disabled={!url.startsWith('https://') || events.length === 0 || create.isPending}
              >
                {t.developers.addWebhook}
              </Button>
            </div>
          </form>
          <ActionError error={create.error ?? toggle.error ?? remove.error} />
          {secret ? <OneTimeSecret label={t.developers.newSecret} value={secret} /> : null}
        </CardContent>
        <QueryView query={subscriptions}>
          {(data) =>
            data.items.length === 0 ? (
              <Empty />
            ) : (
              <Table>
                <TBody>
                  {data.items.map((s) => (
                    <TR key={s.id}>
                      <TD className="font-mono text-xs" dir="ltr">
                        {s.url}
                      </TD>
                      <TD className="text-xs">{s.events.join(', ')}</TD>
                      <TD>
                        <label className="flex items-center gap-1 text-sm">
                          <Checkbox
                            checked={s.active}
                            onChange={(e) => toggle.mutate({ id: s.id, active: e.target.checked })}
                          />
                          {t.developers.active}
                        </label>
                      </TD>
                      <TD>
                        <ConfirmButton
                          label={t.common.delete}
                          title={t.common.delete}
                          message={t.developers.deleteWebhook}
                          variant="destructive"
                          onConfirm={() => remove.mutateAsync(s.id)}
                        />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )
          }
        </QueryView>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t.developers.deliveries}</CardTitle>
        </CardHeader>
        <QueryView query={deliveries}>
          {(data) =>
            data.items.length === 0 ? (
              <Empty />
            ) : (
              <Table>
                <TBody>
                  {data.items.map((d) => (
                    <TR key={d.id}>
                      <TD className="whitespace-nowrap">{instant(d.createdAt, tz, language)}</TD>
                      <TD className="font-mono text-xs" dir="ltr">
                        {d.eventType}
                      </TD>
                      <TD>
                        <Badge tone={DELIVERY_TONE[d.status]}>
                          {t.developers.deliveryStatuses[d.status]}
                        </Badge>
                      </TD>
                      <TD className="tabular-nums">
                        {count(d.attempts, t.developers.attempts)}
                        {d.lastStatusCode ? ` (HTTP ${d.lastStatusCode})` : ''}
                      </TD>
                      <TD>
                        {d.status === 'undelivered' || d.status === 'failed' ? (
                          <Button size="sm" variant="outline" onClick={() => replay.mutate(d.id)}>
                            {t.developers.replay}
                          </Button>
                        ) : null}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )
          }
        </QueryView>
      </Card>
    </>
  );
}

export function DevelopersSection() {
  return (
    <div className="flex flex-col gap-4">
      <ApiKeys />
      <RequestLog />
      <Webhooks />
    </div>
  );
}
